<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";

const props = defineProps({
  title: { type: String, default: "Rename" },
  initialName: { type: String, default: "" },
  showDescription: { type: Boolean, default: false },
  initialDescription: { type: String, default: "" },
  isSaving: { type: Boolean, default: false },
});
const emit = defineEmits(["close", "save"]);

const name = ref(props.initialName);
const description = ref(props.initialDescription);

function close() { emit("close"); }
function onEsc(e) { if (e.key === "Escape") close(); }
onMounted(() => document.addEventListener("keydown", onEsc));
onBeforeUnmount(() => document.removeEventListener("keydown", onEsc));

function save() {
  if (!name.value.trim() || props.isSaving) return;
  emit("save", { name: name.value.trim(), description: description.value.trim() });
}
</script>

<template>
  <div class="fixed inset-0 bg-black/20 dark:bg-black/60 z-[500] flex items-center justify-center p-4" @click.self="close">
    <div class="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-[440px] flex flex-col overflow-hidden z-[520]">

      <div class="flex items-center justify-between px-6 py-4 shrink-0">
        <h2 class="text-lg font-bold text-slate-800 dark:text-slate-100 m-0">{{ title }}</h2>
        <button
          class="flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-600 dark:hover:text-slate-200 border-0 bg-transparent cursor-pointer transition-colors"
          @click="close"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
        </button>
      </div>

      <div class="h-px bg-slate-200 dark:bg-slate-700 shrink-0" />

      <div class="p-6 flex flex-col gap-4">
        <div>
          <label class="block text-sm font-bold text-slate-700 dark:text-slate-200 mb-2">Name</label>
          <input
            v-model="name"
            type="text"
            autofocus
            class="w-full bg-slate-100 dark:bg-slate-800 border-0 rounded-xl px-4 py-3 text-sm text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 outline-none focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-900 transition-shadow"
            @keydown.enter="save"
          />
        </div>

        <div v-if="showDescription">
          <label class="block text-sm font-bold text-slate-700 dark:text-slate-200 mb-2">Description</label>
          <textarea
            v-model="description"
            rows="3"
            class="w-full bg-slate-100 dark:bg-slate-800 border-0 rounded-xl px-4 py-3 text-sm text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 outline-none focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-900 transition-shadow resize-none"
          />
        </div>
      </div>

      <div class="flex items-center justify-end gap-3 px-6 pb-6">
        <button
          class="px-6 py-2.5 border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors cursor-pointer"
          @click="close"
        >
          Cancel
        </button>
        <button
          :disabled="!name.trim() || isSaving"
          class="px-6 py-2.5 bg-slate-800 dark:bg-slate-700 text-white rounded-xl text-sm font-semibold hover:bg-slate-700 dark:hover:bg-slate-600 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-default"
          @click="save"
        >
          {{ isSaving ? "Saving…" : "Save" }}
        </button>
      </div>
    </div>
  </div>
</template>
